import { useErrorReference } from '@core/observability/use-error-reference.js';
import type { AppError } from '@noma-eco/platform/schema';
import type { ReactNode } from 'react';

import { DebugBlock } from '../components/debug-block.js';
import { resolveAppError } from '../lib/resolve-app-error.js';
import { Button } from '../ui/button.js';

export type ErrorPanelProps = {
  /**
   * When set, the panel fires `useErrorReference({ code })` once on mount and
   * renders a `DebugBlock` with the support reference. Omit for "calm" panels
   * (user-initiated cancel screens that aren't anomalies).
   */
  code?: string;
  /**
   * The backend `requestId` when the failure was an `ApiError`. When present,
   * the `DebugBlock` shows it (and no client log is minted — the backend
   * already recorded the error). When absent, a client UUID is minted + logged.
   */
  requestId?: string | undefined;
  /**
   * Optional slot rendered at the top of the card, above the title — used by
   * full-screen surfaces that carry the brand logo (e.g. the `/me` bootstrap
   * error, where no app shell exists around the panel).
   */
  header?: ReactNode;
  title: string;
  description: string;
  /**
   * When present, the wire-envelope errors render as code-keyed rows between
   * the description and the action buttons. `null` and `undefined` both
   * suppress the rows region.
   */
  errors?: AppError[] | null;
  /**
   * The underlying thrown error's `name` + `message`, for failures that
   * carry no `AppError` envelope (a non-`ApiError` throw — connectivity loss,
   * a token-refresh failure, an aborted body read). Logged (never rendered) so
   * the Sentry log can distinguish those otherwise-indistinguishable failure
   * modes. Holds the JS `Error` name/message only — no user data.
   */
  cause?: { name: string; message: string } | null;
  primaryAction: { label: string; onClick: () => void };
  secondaryAction?: { label: string; onClick: () => void };
  /** Override the root `data-testid` (default `error-panel`). */
  testId?: string;
};

/**
 * The canonical full-card error surface — centered card with title +
 * description + (optional) AppError rows + 1-2 action buttons + (optional)
 * support reference. Used by `MeBootstrapErrorPanel`, `SessionInvalidatedPanel`,
 * `AppCrashPanel`, `CallbackErrorScreen` / `CallbackCancelled`, and inline at
 * the 3 page-tier error sites. See
 * `docs/features/error-surface-architecture/design.md`.
 */
export function ErrorPanel({
  code,
  requestId,
  header,
  title,
  description,
  errors,
  cause,
  primaryAction,
  secondaryAction,
  testId,
}: ErrorPanelProps) {
  return (
    <main
      className="flex min-h-[60vh] items-center justify-center bg-background px-6"
      data-testid={testId ?? 'error-panel'}
    >
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-8 text-card-foreground shadow-sm">
        {header}
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>

        {errors !== null && errors !== undefined && errors.length > 0 ? (
          <ul className="space-y-2 text-destructive">
            {errors.map((error, idx) => {
              const { main, technical } = resolveAppError(error);
              return (
                <li key={idx} data-testid="error-panel-row">
                  <div>{main}</div>
                  {technical !== undefined ? (
                    <div
                      className="mt-0.5 text-xs text-muted-foreground"
                      data-testid="error-panel-technical"
                    >
                      {technical}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}

        <div className="flex flex-col gap-2">
          <Button
            data-testid="error-panel-primary-btn"
            className="w-full"
            size="lg"
            onClick={primaryAction.onClick}
          >
            {primaryAction.label}
          </Button>
          {secondaryAction !== undefined ? (
            <Button
              data-testid="error-panel-secondary-btn"
              className="w-full"
              size="lg"
              variant="outline"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          ) : null}
        </div>

        {code !== undefined ? (
          <ReportedDebug
            code={code}
            requestId={requestId}
            errors={errors}
            cause={cause}
          />
        ) : null}
      </div>
    </main>
  );
}

/**
 * Calls `useErrorReference` and renders the matching `DebugBlock`. Pulled into
 * a separate component so the hook only fires when `code` is set (hooks can't
 * be conditionally called from the parent).
 *
 * When `requestId` is set (an `ApiError`), the `DebugBlock` shows the backend
 * id and no client log is minted; the `logContext` below is emitted only on the
 * minted-UUID path (a non-`ApiError` failure the backend never recorded).
 *
 * The wire-envelope error codes ride along as `logContext` so the Sentry log
 * records *why* the surface fired. `hasErrors: false` distinguishes a
 * connectivity failure (no `AppError` envelope) from a backend rejection.
 *
 * `firstMessage` carries the backend's canonical English message for the code
 * (static per code — per-occurrence variance lives in `details`, which is kept
 * out of the log to avoid leaking PII).
 *
 * `errorName` / `errorMessage` carry the underlying throw for the
 * envelope-less case (`hasErrors: false`): without them a connectivity loss, a
 * token-refresh failure, and an aborted read all log identically.
 */
function ReportedDebug({
  code,
  requestId,
  errors,
  cause,
}: {
  code: string;
  requestId: string | undefined;
  errors: AppError[] | null | undefined;
  cause: { name: string; message: string } | null | undefined;
}) {
  const { reference, timestamp } = useErrorReference({
    requestId,
    code,
    logMessage: `[ErrorPanel] ${code}`,
    logContext: {
      hasErrors: errors != null && errors.length > 0,
      firstCode: errors?.[0]?.code ?? null,
      firstMessage: errors?.[0]?.message ?? null,
      errorCount: errors?.length ?? 0,
      errorName: cause?.name ?? null,
      errorMessage: cause?.message ?? null,
    },
  });
  return <DebugBlock code={code} reference={reference} timestamp={timestamp} />;
}
